import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";
import {
  getCategories,
  getCategoryById,
  createCategory,
  updateCategory,
  deleteCategory,
} from "services";
import { handleAPIError, queryClient } from "utils";
import { CategoryType } from "types";
import { toast } from "react-toastify";

// Fetch all categories
export const useGetCategories = () => {
  const queryResult = useQuery({
    queryKey: [queryKeys.categories],
    queryFn: async () => await getCategories(),
  });

  if (queryResult.isError) {
    handleAPIError(queryResult.error, true);
  }

  return queryResult;
};

// Fetch a single category by ID
export const useGetCategoryById = (id) => {
  return useQuery({
    queryKey: [queryKeys.categories, id], // Separate key for fetching single category
    queryFn: async () => await getCategoryById(id),
    enabled: !!id, // Only run query if ID is provided
  });
};

// Create a new category
export const useCreateCategory = () => {
  return useMutation({
    mutationFn: createCategory,
    onSuccess: async () => {
      toast.success("Category created successfully");

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.categories],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

type UpdateCategoryPayload = {
  id: string;
  data: CategoryType;
};

// Update the useUpdateCategory hook
export const useUpdateCategory = (): UseMutationResult<
  any,
  Error,
  UpdateCategoryPayload
> => {
  return useMutation<any, Error, UpdateCategoryPayload>({
    mutationFn: ({ id, data }: UpdateCategoryPayload) =>
      updateCategory(id, data),
    onSuccess: async () => {
      toast.success("Category updated successfully");
      // Invalidate categories query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.categories],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Delete a category
export const useDeleteCategory = () => {
  return useMutation({
    mutationFn: deleteCategory,
    onSuccess: async () => {
      toast.success("Category deleted successfully");

      await queryClient.invalidateQueries({
        queryKey: [queryKeys.categories],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
